import type { IsoDate } from './types.js';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;
const MONTH_NAMES_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
const DAY_NAMES_LONG = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false;
  const m = ISO_DATE_RE.exec(value);
  if (!m) return false;
  // Round-trip through a local Date to reject 2026-02-30 and friends.
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return (
    date.getFullYear() === Number(y) &&
    date.getMonth() === Number(mo) - 1 &&
    date.getDate() === Number(d)
  );
}

/**
 * `YYYY-MM-DD` → a Date at local midnight. Parsing with `new Date(iso)` would treat the
 * string as UTC and render as the previous day west of Greenwich (§3).
 */
export function toLocalDate(iso: IsoDate): Date {
  if (!isIsoDate(iso)) throw new Error(`not a YYYY-MM-DD date: ${iso}`);
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function toIsoDate(date: Date): IsoDate {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function todayIso(now: Date = new Date()): IsoDate {
  return toIsoDate(now);
}

/**
 * Today's local date in a named timezone. The API runs on a Lambda whose clock is UTC, so an
 * 8pm-Eastern request would otherwise be told it is already tomorrow.
 */
export function todayIsoInZone(timeZone: string, now: Date = new Date()): IsoDate {
  // en-CA formats as YYYY-MM-DD, which is exactly the wire format.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * An ISO timestamp (`updatedAt` and friends, always stamped in UTC) → the calendar date it
 * falls on *here*. Slicing the first 10 characters instead would show the UTC date, so an
 * evening change east of Greenwich reads as tomorrow (§3).
 */
export function localIsoDateOf(timestamp: string): IsoDate {
  return toIsoDate(new Date(timestamp));
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(iso: IsoDate): number {
  return toLocalDate(iso).getDay();
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const d = toLocalDate(iso);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

/**
 * Sunday-start week, so a Tuesday and Thursday session share one "this week" and the week
 * turns over on the same day the calendar on the wall does. The consequence worth stating:
 * the Saturday after a Tuesday session, that session is still "this week" — it's Sunday
 * before "this week" starts naming a match that hasn't been played yet.
 */
export function startOfWeek(iso: IsoDate): IsoDate {
  return addDays(iso, -dayOfWeek(iso));
}

/** The Sunday that opens the week after `iso`'s. */
export function startOfNextWeek(iso: IsoDate): IsoDate {
  return addDays(startOfWeek(iso), 7);
}

export function isSameWeek(a: IsoDate, b: IsoDate): boolean {
  return startOfWeek(a) === startOfWeek(b);
}

/** `2026-09-08` → `Tue Sep 8`. */
export function formatDateShort(iso: IsoDate): string {
  const d = toLocalDate(iso);
  return `${DAY_NAMES[d.getDay()]} ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}

/** `2026-09-08` → `Tuesday`, for the reminder text the group pastes into its chat. */
export function formatDayLong(iso: IsoDate): string {
  return DAY_NAMES_LONG[toLocalDate(iso).getDay()] as string;
}

/**
 * `2026-09-08` → `Tue`, the day half of a match card's day line. Abbreviated because that line
 * is three `•`-separated parts on one row: spelled out, "WEDNESDAY" wraps the line onto two on
 * a phone, which costs more than the four letters save.
 */
export function formatDayShort(iso: IsoDate): string {
  return DAY_NAMES[toLocalDate(iso).getDay()] as string;
}

/** `2026-09-08` → `Sep 8`, the date half of a match card's day line. */
export function formatMonthDay(iso: IsoDate): string {
  const d = toLocalDate(iso);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}

/** `2026-09-08` → `Sep-8`, the date as the reminder text spells it. */
export function formatMonthDayCompact(iso: IsoDate): string {
  const d = toLocalDate(iso);
  return `${MONTH_NAMES[d.getMonth()]}-${d.getDate()}`;
}

/** `2026-09-08` → `September 2026`, for month headers on the season screen. */
export function formatMonthHeading(iso: IsoDate): string {
  const d = toLocalDate(iso);
  return `${MONTH_NAMES_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * An ISO timestamp → `Aug 28, 2026, 2:33 PM`, in the reader's own zone. Unlike the plain dates
 * this app is otherwise made of, a `firstSeenAt` is a real instant, so there is nothing to
 * resolve in `America/New_York`: whoever is reading it is standing in the zone they want (§3).
 */
export function formatTimestamp(timestamp: string): string {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return timestamp;
  const hours = d.getHours();
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return (
    `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ` +
    `${h12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`
  );
}

/** `18:30` → `6:30pm`; `18:00` → `6pm`. */
export function formatTime(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const hours = Number(m[1]);
  const minutes = m[2] as string;
  const suffix = hours >= 12 ? 'pm' : 'am';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === '00' ? `${h12}${suffix}` : `${h12}:${minutes}${suffix}`;
}

/**
 * `18:00`, `20:00` → `6:00 - 8:00`. Minutes are always shown, unlike {@link formatTime}: the
 * two ends of a range should be the same width, so `6 - 8:30` never happens. No am/pm — every
 * session in this app is an evening one, so it would be two characters carrying no news.
 */
export function formatTimeRange(start: string, end: string): string {
  return `${to12Hour(start)} - ${to12Hour(end)}`;
}

/**
 * `18:30`, `20:30` → `6:30-8:30`, the court-time range in the reminder text. Tighter than
 * {@link formatTimeRange} because it sits mid-sentence rather than on a line of its own.
 */
export function formatTimeRangeCompact(start: string, end: string): string {
  return `${to12Hour(start)}-${to12Hour(end)}`;
}

function to12Hour(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const hours = Number(m[1]);
  return `${hours % 12 === 0 ? 12 : hours % 12}:${m[2]}`;
}

export function isValidTime(hhmm: unknown): boolean {
  if (typeof hhmm !== 'string') return false;
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return false;
  return Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/**
 * Season id sanity: `YYYY-YY`, second half one greater than the first (§3).
 * Cheap to check, and it catches the `2026-2027` and `2026-26` typos at import time.
 */
export function isSeasonId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return false;
  const start = Number(m[1]);
  const expected = String((start + 1) % 100).padStart(2, '0');
  return m[2] === expected;
}
